/**
 * useVoiceChat
 * ElevenLabs ConvAI integration for the Talk with Martha voice mode.
 *
 * Uses the @elevenlabs/react `useConversation` hook for full ConvAI
 * session management including audio levels, frequency data, feedback,
 * and contextual updates.
 *
 * Falls back to text-only /api/tts endpoint for TTS of text-mode responses.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useConversation } from '@elevenlabs/react';
import type { Status } from '@elevenlabs/react';

export type VoiceStatus = 'idle' | 'connecting' | 'connected' | 'speaking' | 'listening' | 'error';

export interface UseVoiceChatResult {
  /** Current voice session status */
  voiceStatus: VoiceStatus;
  /** Whether voice mode is currently active */
  isVoiceActive: boolean;
  /** Start a voice conversation session */
  startVoice: () => Promise<void>;
  /** Stop the voice session */
  stopVoice: () => void;
  /** Play TTS audio for a given text (text-mode fallback) */
  playTts: (text: string) => Promise<void>;
  /** Whether TTS audio is currently playing */
  isTtsPlaying: boolean;
  /** Stop TTS playback */
  stopTts: () => void;
  /** Current audio level (0-1) for visualizer */
  audioLevel: number;
  /** Input frequency data for bar visualizer */
  inputFrequencyData: Uint8Array | undefined;
  /** Output frequency data for bar visualizer */
  outputFrequencyData: Uint8Array | undefined;
  /** Send a text message into the voice session */
  sendUserMessage: (text: string) => void;
  /** Send contextual update to the voice session */
  sendContextualUpdate: (text: string) => void;
  /** Send feedback (thumbs up/down) for the conversation */
  sendFeedback: (like: boolean) => void;
  /** Whether feedback can be sent */
  canSendFeedback: boolean;
  /** Whether the agent is currently speaking */
  isSpeaking: boolean;
  /** Conversation ID of the last ended voice session (for post-call summary) */
  lastConversationId: string | null;
  /** Dismiss the post-call summary */
  dismissSummary: () => void;
}

const ELEVENLABS_AGENT_ID = import.meta.env.VITE_ELEVENLABS_AGENT_ID ?? '';

function mapStatus(status: Status): VoiceStatus {
  switch (status) {
    case 'connecting': return 'connecting';
    case 'connected': return 'connected';
    case 'disconnecting':
    case 'disconnected': return 'idle';
    default: return 'idle';
  }
}

export function useVoiceChat(): UseVoiceChatResult {
  const [voiceStatus, setVoiceStatus] = useState<VoiceStatus>('idle');
  const [isVoiceActive, setIsVoiceActive] = useState(false);
  const [isTtsPlaying, setIsTtsPlaying] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [inputFrequencyData, setInputFrequencyData] = useState<Uint8Array | undefined>();
  const [outputFrequencyData, setOutputFrequencyData] = useState<Uint8Array | undefined>();
  const [lastConversationId, setLastConversationId] = useState<string | null>(null);

  const ttsAudioRef = useRef<HTMLAudioElement | null>(null);
  const animFrameRef = useRef<number>(0);
  const activeConversationIdRef = useRef<string | null>(null);

  const conversation = useConversation({
    onConnect: () => {
      console.log('[Voice] Connected');
      setVoiceStatus('connected');
      setIsVoiceActive(true);
    },
    onDisconnect: () => {
      console.log('[Voice] Disconnected');
      // Surface the conversation ID for the post-call summary card
      if (activeConversationIdRef.current) {
        setLastConversationId(activeConversationIdRef.current);
        activeConversationIdRef.current = null;
      }
      setVoiceStatus('idle');
      setIsVoiceActive(false);
      setAudioLevel(0);
      setInputFrequencyData(undefined);
      setOutputFrequencyData(undefined);
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
        animFrameRef.current = 0;
      }
    },
    onError: (message) => {
      console.error('[Voice] Error:', message);
      setVoiceStatus('error');
    },
    onModeChange: ({ mode }) => {
      setVoiceStatus(mode === 'speaking' ? 'speaking' : 'listening');
    },
    onStatusChange: ({ status }) => {
      setVoiceStatus(mapStatus(status));
      if (status === 'disconnected') {
        setIsVoiceActive(false);
      }
    },
    onMessage: ({ message, source }) => {
      console.log(`[Voice] ${source}: ${message}`);
    },
  });

  // Poll audio levels and frequency data
  useEffect(() => {
    if (!isVoiceActive) return;

    function poll() {
      try {
        const input = conversation.getInputVolume();
        const output = conversation.getOutputVolume();
        setAudioLevel(Math.max(input, output));

        const inputFreq = conversation.getInputByteFrequencyData();
        const outputFreq = conversation.getOutputByteFrequencyData();
        if (inputFreq) setInputFrequencyData(inputFreq);
        if (outputFreq) setOutputFrequencyData(outputFreq);
      } catch {
        // conversation may have ended
      }
      animFrameRef.current = requestAnimationFrame(poll);
    }
    poll();

    return () => {
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
        animFrameRef.current = 0;
      }
    };
  }, [isVoiceActive, conversation]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      ttsAudioRef.current?.pause();
    };
  }, []);

  /**
   * Start a voice conversation with ElevenLabs ConvAI.
   */
  const startVoice = useCallback(async () => {
    if (!ELEVENLABS_AGENT_ID) {
      console.warn('VITE_ELEVENLABS_AGENT_ID not configured');
      setVoiceStatus('error');
      return;
    }

    if (isVoiceActive) return;

    try {
      setVoiceStatus('connecting');
      setLastConversationId(null); // dismiss any previous summary

      // Request microphone permission
      await navigator.mediaDevices.getUserMedia({ audio: true });

      const convId = await conversation.startSession({ agentId: ELEVENLABS_AGENT_ID });
      if (convId) activeConversationIdRef.current = convId;
    } catch (err) {
      console.error('Failed to start voice session:', err);
      setVoiceStatus('error');
    }
  }, [isVoiceActive, conversation]);

  /**
   * Stop the active voice conversation.
   */
  const stopVoice = useCallback(async () => {
    try {
      await conversation.endSession();
    } catch {
      // may already be disconnected
    }
    setVoiceStatus('idle');
    setIsVoiceActive(false);
    setAudioLevel(0);
  }, [conversation]);

  /**
   * Play TTS audio for a text response (text-mode fallback).
   */
  const playTts = useCallback(async (text: string) => {
    if (!text.trim()) return;

    try {
      setIsTtsPlaying(true);

      const response = await fetch('/api/tts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text }),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`TTS failed: ${response.status}`);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);

      // Stop any currently playing audio
      if (ttsAudioRef.current) {
        ttsAudioRef.current.pause();
        ttsAudioRef.current = null;
      }

      const audio = new Audio(url);
      ttsAudioRef.current = audio;

      audio.onended = () => {
        setIsTtsPlaying(false);
        URL.revokeObjectURL(url);
        ttsAudioRef.current = null;
      };

      audio.onerror = () => {
        setIsTtsPlaying(false);
        URL.revokeObjectURL(url);
        ttsAudioRef.current = null;
      };

      await audio.play();
    } catch (err) {
      console.error('TTS playback error:', err);
      setIsTtsPlaying(false);
    }
  }, []);

  /**
   * Dismiss the post-call summary card.
   */
  const dismissSummary = useCallback(() => {
    setLastConversationId(null);
  }, []);

  /**
   * Stop TTS playback.
   */
  const stopTts = useCallback(() => {
    if (ttsAudioRef.current) {
      ttsAudioRef.current.pause();
      ttsAudioRef.current = null;
    }
    setIsTtsPlaying(false);
  }, []);

  return {
    voiceStatus,
    isVoiceActive,
    startVoice,
    stopVoice,
    playTts,
    isTtsPlaying,
    stopTts,
    audioLevel,
    inputFrequencyData,
    outputFrequencyData,
    sendUserMessage: conversation.sendUserMessage,
    sendContextualUpdate: conversation.sendContextualUpdate,
    sendFeedback: conversation.sendFeedback,
    canSendFeedback: conversation.canSendFeedback,
    isSpeaking: conversation.isSpeaking,
    lastConversationId,
    dismissSummary,
  };
}
